/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Step 3: Validating your migration", description = "Compare Hightouch Events data with your previous event tracking platform before cutover.", lastUpdatedDate = new Date(1782259200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Step 3: Validating your migration",
        description: "Compare Hightouch Events data with your previous event tracking platform before cutover.",
        lastUpdatedDate: "Wed Jun 24 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Verify your setup",
          "depth": 0,
          "id": "verify-your-setup"
        }, {
          "value": "Check data quality",
          "depth": 0,
          "id": "check-data-quality"
        }, {
          "value": "Compare event volume",
          "depth": 1,
          "id": "compare-event-volume"
        }, {
          "value": "Compare property values",
          "depth": 1,
          "id": "compare-property-values"
        }, {
          "value": "What differences to expect between your old provider and Hightouch Events",
          "depth": 0,
          "id": "what-differences-to-expect-between-your-old-provider-and-hightouch-events"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      ol: "ol",
      li: "li",
      a: "a",
      h2: "h2",
      strong: "strong",
      ul: "ul",
      h3: "h3",
      code: "code",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "After Hightouch Events is running alongside your existing system, compare the two data streams before you cut over."
      }), "\n", _jsx(_components.p, {
        children: "There are two main steps in validation:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Verify that event data is flowing from Hightouch Events with the correct properties and data types."
        }), "\n", _jsx(_components.li, {
          children: "Check that the data from Hightouch Events is comparable to the data from your existing provider in volume and in values, such as user IDs and event properties."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Set up ", _jsx(_components.a, {
          href: "/events/monitoring",
          children: "monitoring and alerts"
        }), " while both systems run, so you catch volume drops or contract violations early instead of discovering them at cutover."]
      }), "\n", _jsx(_components.h2, {
        id: "verify-your-setup",
        children: "Verify your setup"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Check event reception."
          }), "\n", _jsxs(_components.ul, {
            children: ["\n", _jsx(_components.li, {
              children: "Confirm that both your current system and Hightouch Events receive events."
            }), "\n", _jsxs(_components.li, {
              children: ["Use the ", _jsx(_components.a, {
                href: "/events/sources/overview#debugger",
                children: "debugger"
              }), " to view incoming events in real time."]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Verify event structure."
          }), "\n", _jsxs(_components.ul, {
            children: ["\n", _jsx(_components.li, {
              children: "Ensure all expected properties are present in the Hightouch events."
            }), "\n", _jsx(_components.li, {
              children: "Check that data types are correct (for example, numbers aren't being sent as strings)."
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Test all event types."
          }), "\n", _jsxs(_components.ul, {
            children: ["\n", _jsx(_components.li, {
              children: "Manually trigger each type of event (page views, user identifications, custom events) in your application."
            }), "\n", _jsx(_components.li, {
              children: "Verify that they appear correctly in both systems."
            }), "\n"]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "check-data-quality",
        children: "Check data quality"
      }), "\n", _jsx(_components.p, {
        children: "Compare the data between your current platform and Hightouch Events to make sure that Hightouch Events is instrumented correctly and sending the data you expect. We recommend checking both event volume and values."
      }), "\n", _jsx(_components.p, {
        children: "If you're maintaining your data model during your migration or only making minor changes, validating data values will be more straightforward. Significant changes to the data model during migration will make validation more complex and time-consuming."
      }), "\n", _jsx(_components.h3, {
        id: "compare-event-volume",
        children: "Compare event volume"
      }), "\n", _jsxs(_components.p, {
        children: ["Let's assume that you're migrating from Segment to Hightouch Events, and have data flowing from Segment into tables per Segment's schema and from Hightouch Events into tables per Hightouch's schema. We'll also assume events from during the migration carry the ", _jsx(_components.code, {
          children: "migrationId"
        }), " attached by the wrapper in Step 2, which Hightouch writes to the ", _jsx(_components.code, {
          children: "context_migration_id"
        }), " column."]
      }), "\n", _jsx(_components.p, {
        children: "The examples below use Segment table and column names to keep them concrete. The approach is identical for any source—if you're migrating from RudderStack or mParticle, substitute your provider's table and column names. See the RudderStack and mParticle guides for platform-specific notes."
      }), "\n", _jsxs(_components.p, {
        children: ["We could use the following query—or something adjusted to your warehouse and setup—to look at the count of ", _jsx(_components.code, {
          children: "identifies"
        }), " events within the last 7 days. While we expect to see roughly the same count of events, there can be some variation—we'll cover why differences can occur later in this step of the guide."]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "WITH"
          }), " segment_identifies ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " (\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), " ", _jsx(_components.span, {
            className: "hljs-type",
            children: "DATE"
          }), "(", _jsx(_components.span, {
            className: "hljs-type",
            children: "timestamp"
          }), ") ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " event_date, context_migration_id, ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "COUNT"
          }), "(", _jsx(_components.span, {
            className: "hljs-operator",
            children: "*"
          }), ") ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " segment_count\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "FROM"
          }), " SEGMENT.identifies\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "WHERE"
          }), " ", _jsx(_components.span, {
            className: "hljs-type",
            children: "timestamp"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">="
          }), " DATEADD(", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "day"
          }), ", ", _jsx(_components.span, {
            className: "hljs-number",
            children: "-7"
          }), ", ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "CURRENT_DATE"
          }), "())\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GROUP"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "BY"
          }), " ", _jsx(_components.span, {
            className: "hljs-type",
            children: "DATE"
          }), "(", _jsx(_components.span, {
            className: "hljs-type",
            children: "timestamp"
          }), "), context_migration_id\n),\nhightouch_identifies ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " (\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), " ", _jsx(_components.span, {
            className: "hljs-type",
            children: "DATE"
          }), "(", _jsx(_components.span, {
            className: "hljs-type",
            children: "timestamp"
          }), ") ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " event_date, context_migration_id, ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "COUNT"
          }), "(", _jsx(_components.span, {
            className: "hljs-operator",
            children: "*"
          }), ") ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " hightouch_count\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "FROM"
          }), " HIGHTOUCH.identifies\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "WHERE"
          }), " ", _jsx(_components.span, {
            className: "hljs-type",
            children: "timestamp"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">="
          }), " DATEADD(", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "day"
          }), ", ", _jsx(_components.span, {
            className: "hljs-number",
            children: "-7"
          }), ", ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "CURRENT_DATE"
          }), "())\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GROUP"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "BY"
          }), " ", _jsx(_components.span, {
            className: "hljs-type",
            children: "DATE"
          }), "(", _jsx(_components.span, {
            className: "hljs-type",
            children: "timestamp"
          }), "), context_migration_id\n)\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "COALESCE"
          }), "(s.event_date, h.event_date) ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " event_date,\n  s.context_migration_id,\n  s.segment_count,\n  h.hightouch_count,\n  s.segment_count ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "-"
          }), " h.hightouch_count ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " count_difference,\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "CASE"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "WHEN"
          }), " s.segment_count ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " h.hightouch_count ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "THEN"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'Match'"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ELSE"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'Mismatch'"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "END"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " comparison_result\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "FROM"
          }), " segment_identifies s\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "FULL"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "OUTER"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "JOIN"
          }), " hightouch_identifies h ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " s.event_date ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " h.event_date ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AND"
          }), " s.context_migration_id ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " h.context_migration_id\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "WHERE"
          }), " s.context_migration_id ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "IS"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NOT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NULL"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "OR"
          }), " h.context_migration_id ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "IS"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NOT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NULL"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ORDER"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "BY"
          }), " event_date, context_migration_id;\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "compare-property-values",
        children: "Compare property values"
      }), "\n", _jsx(_components.p, {
        children: "We also need to validate that Hightouch Events is collecting the same values as your prior provider."
      }), "\n", _jsxs(_components.p, {
        children: ["The query below looks at a selection of properties relevant to ", _jsx(_components.code, {
          children: "identifies"
        }), " calls and compares between the two platforms over a 7-day period."]
      }), "\n", _jsx(_components.p, {
        children: "You can modify this query to examine different properties, use a different time window, or look at data in a narrower set of dates."
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "WITH"
          }), " segment_data ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " (\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), "\n    id ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " segment_id,\n    context_migration_id,\n    anonymous_id,\n    user_id,\n    ", _jsx(_components.span, {
            className: "hljs-type",
            children: "timestamp"
          }), ",\n    email,\n    name\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "FROM"
          }), " SEGMENT.identifies\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "WHERE"
          }), " ", _jsx(_components.span, {
            className: "hljs-type",
            children: "timestamp"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">="
          }), " DATEADD(", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "day"
          }), ", ", _jsx(_components.span, {
            className: "hljs-number",
            children: "-7"
          }), ", ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "CURRENT_DATE"
          }), "())\n),\nhightouch_data ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " (\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), "\n    id ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " hightouch_id,\n    context_migration_id,\n    anonymous_id,\n    user_id,\n    ", _jsx(_components.span, {
            className: "hljs-type",
            children: "timestamp"
          }), ",\n    email,\n    name\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "FROM"
          }), " HIGHTOUCH.identifies\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "WHERE"
          }), " ", _jsx(_components.span, {
            className: "hljs-type",
            children: "timestamp"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: ">="
          }), " DATEADD(", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "day"
          }), ", ", _jsx(_components.span, {
            className: "hljs-number",
            children: "-7"
          }), ", ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "CURRENT_DATE"
          }), "())\n)\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), "\n  s.context_migration_id,\n  ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "COALESCE"
          }), "(s.user_id, h.user_id) ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " user_id,\n  s.segment_id,\n  h.hightouch_id,\n  s.anonymous_id ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " segment_anonymous_id,\n  h.anonymous_id ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " hightouch_anonymous_id,\n  s.timestamp ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " segment_timestamp,\n  h.timestamp ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " hightouch_timestamp,\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "CASE"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "WHEN"
          }), " s.email ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " h.email ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "THEN"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'Match'"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ELSE"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'Mismatch'"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "END"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " email_comparison,\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "CASE"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "WHEN"
          }), " s.name ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " h.name ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "THEN"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'Match'"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ELSE"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'Mismatch'"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "END"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " name_comparison,\n  s.email ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " segment_email,\n  h.email ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " hightouch_email,\n  s.name ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " segment_name,\n  h.name ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " hightouch_name\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "FROM"
          }), " segment_data s\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "FULL"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "OUTER"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "JOIN"
          }), " hightouch_data h ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " s.context_migration_id ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " h.context_migration_id\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "WHERE"
          }), "\n  s.email ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "!="
          }), " h.email\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "OR"
          }), " s.name ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "!="
          }), " h.name\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "OR"
          }), " s.anonymous_id ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "!="
          }), " h.anonymous_id\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "OR"
          }), " s.context_migration_id ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "IS"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NULL"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "OR"
          }), " h.context_migration_id ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "IS"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NULL"
          }), "\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ORDER"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "BY"
          }), " s.timestamp ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "DESC"
          }), "\nLIMIT ", _jsx(_components.span, {
            className: "hljs-number",
            children: "100"
          }), ";  ", _jsx(_components.span, {
            className: "hljs-comment",
            children: "-- Limit to 100 rows for a manageable sample"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h2, {
        id: "what-differences-to-expect-between-your-old-provider-and-hightouch-events",
        children: "What differences to expect between your old provider and Hightouch Events"
      }), "\n", _jsx(_components.p, {
        children: "Event volume should be approximately the same, but\nsome discrepancies between systems are normal. Deployment rollout, ad blockers, and network errors could all cause events to appear in one tool but not another, leading to differences in volume."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Timing differences"
          }), ": Events may be processed in slightly different orders or with small time variations."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Dropped events"
          }), ": Network issues might cause events to be lost in one system but not the other."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Duplicate events"
          }), ": Some events might be sent twice in edge cases (for example, page reloads)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Blocking behavior"
          }), ": Other providers might not block events as strictly as Hightouch does when running type checks."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch Events should collect the same values as your previous event collection provider, though there may be minor variations in ", _jsx(_components.a, {
          href: "/events/event-spec",
          children: "automatically collected fields"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["In the next section, we'll explore how to ", _jsx(_components.a, {
          href: "unifying-data",
          children: "unify your historical data with new Hightouch data"
        }), "."]
      })]
    });
  }
}
export default MDXContent;
